import * as THREE from 'three';

const canvasContainer = document.getElementById('centauri-canvas');

if (canvasContainer) {
    let width = window.innerWidth;
    let height = window.innerHeight;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    canvasContainer.appendChild(renderer.domElement);

    // Scene
    const scene = new THREE.Scene();

    // Camera (Top Down)
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 2000);
    camera.position.set(0, 150, 0); // High Y position
    camera.lookAt(new THREE.Vector3(0, 0, 0));

    // Starfield Background
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 5000;
    const starPositions = new Float32Array(starCount * 3);
    const starColors = new Float32Array(starCount * 3);

    for (let i = 0; i < starCount; i++) {
        const x = (Math.random() - 0.5) * 2000;
        const y = (Math.random() - 0.5) * 2000; // Flat distribution in Y for top down depth? or sphere? 
        // Let's keep a sphere distribution so it looks good from top too
        const z = (Math.random() - 0.5) * 2000;
        starPositions[i * 3] = x;
        starPositions[i * 3 + 1] = y;
        starPositions[i * 3 + 2] = z;

        const brightness = 0.5 + Math.random() * 0.5;
        starColors[i * 3] = brightness;
        starColors[i * 3 + 1] = brightness;
        starColors[i * 3 + 2] = brightness;
    }

    starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    starGeometry.setAttribute('color', new THREE.BufferAttribute(starColors, 3));

    // Generate Circle Texture for Round Stars
    const getCircleTexture = () => {
        const size = 32;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
        ctx.fillStyle = '#ffffff';
        ctx.fill();

        const texture = new THREE.CanvasTexture(canvas);
        return texture;
    };

    const starMaterial = new THREE.PointsMaterial({
        size: 1.5,
        vertexColors: true,
        map: getCircleTexture(),
        transparent: true,
        alphaTest: 0.5, // Crisp circle edges
        opacity: 0.8,
        sizeAttenuation: true
    });

    const starField = new THREE.Points(starGeometry, starMaterial);
    scene.add(starField);

    // --- GLSL SHADERS ---

    // Noise Function (Simplex 3D) - Standard GLSL chunk
    const noiseShaderChunk = `
        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

        float snoise(vec3 v) {
            const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
            const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

            // First corner
            vec3 i  = floor(v + dot(v, C.yyy) );
            vec3 x0 = v - i + dot(i, C.xxx) ;

            // Other corners
            vec3 g = step(x0.yzx, x0.xyz);
            vec3 l = 1.0 - g;
            vec3 i1 = min( g.xyz, l.zxy );
            vec3 i2 = max( g.xyz, l.zxy );

            //   x0 = x0 - 0.0 + 0.0 * C.xxx;
            //   x1 = x0 - i1  + 1.0 * C.xxx;
            //   x2 = x0 - i2  + 2.0 * C.xxx;
            //   x3 = x0 - 1.0 + 3.0 * C.xxx;
            vec3 x1 = x0 - i1 + C.xxx;
            vec3 x2 = x0 - i2 + C.yyy; // 2.0*C.x = 1/3 = C.y
            vec3 x3 = x0 - D.yyy;      // -1.0+3.0*C.x = -0.5 = -D.y

            // Permutations
            i = mod289(i); 
            vec4 p = permute( permute( permute( 
                        i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
                    + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
                    + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

            // Gradients: 7x7 points over a square, mapped onto an octahedron.
            // The ring size 17*17 = 289 is close to a multiple of 49 (49*6 = 294)
            float n_ = 0.142857142857; // 1.0/7.0
            vec3  ns = n_ * D.wyz - D.xzx;

            vec4 j = p - 49.0 * floor(p * ns.z * ns.z);  //  mod(p,7*7)

            vec4 x_ = floor(j * ns.z);
            vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

            vec4 x = x_ *ns.x + ns.yyyy;
            vec4 y = y_ *ns.x + ns.yyyy;
            vec4 h = 1.0 - abs(x) - abs(y);

            vec4 b0 = vec4( x.xy, y.xy );
            vec4 b1 = vec4( x.zw, y.zw );

            vec4 s0 = floor(b0)*2.0 + 1.0;
            vec4 s1 = floor(b1)*2.0 + 1.0;
            vec4 sh = -step(h, vec4(0.0));

            vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
            vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

            vec3 p0 = vec3(a0.xy,h.x);
            vec3 p1 = vec3(a0.zw,h.y);
            vec3 p2 = vec3(a1.xy,h.z);
            vec3 p3 = vec3(a1.zw,h.w);

            //Normalise gradients
            vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
            p0 *= norm.x;
            p1 *= norm.y;
            p2 *= norm.z;
            p3 *= norm.w;

            // Mix final noise value
            vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
            m = m * m;
            return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                        dot(p2,x2), dot(p3,x3) ) );
        }
    `;

    // Star Surface Shaders
    const starVertexShader = `
        varying vec2 vUv;
        varying vec3 vPosition;
        void main() {
            vUv = uv;
            vPosition = position;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `;

    const starFragmentShader = `
        uniform vec3 color1;
        uniform vec3 color2;
        uniform float time;
        uniform float noiseScale;
        
        varying vec2 vUv;
        varying vec3 vPosition;

        ${noiseShaderChunk}

        void main() {
            // Animated 3D noise based on position to minimize seams
            float n = snoise(vec3(vPosition * noiseScale + vec3(0.0, time * 0.1, time * 0.1)));
            
            // Map -1..1 to 0..1, perturbed
            float t = (n + 1.0) * 0.5;
            
            // Sharpen the noise for a "fiery" look
            t = pow(t, 2.0); 

            vec3 finalColor = mix(color2, color1, t); // Mix cool to hot
            gl_FragColor = vec4(finalColor, 1.0);
        }
    `;

    // Star Creation Helper
    const createStar = (radius, colorA, colorB, position, flowSpeed = 1.0) => {
        const group = new THREE.Group();

        // Core (Procedural Shader)
        const coreGeom = new THREE.SphereGeometry(radius, 64, 64); // Higher poly for per-vertex/frag quality
        const coreMat = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                color1: { value: new THREE.Color(colorA) }, // Hotter/Brighter
                color2: { value: new THREE.Color(colorB) }, // Cooler/Darker
                noiseScale: { value: 0.5 } // Texture density
            },
            vertexShader: starVertexShader,
            fragmentShader: starFragmentShader
        });

        const core = new THREE.Mesh(coreGeom, coreMat);
        group.add(core);

        // Glow
        const glowGeom = new THREE.SphereGeometry(radius * 3.0, 32, 32); // Slightly larger
        const glowMat = new THREE.ShaderMaterial({
            uniforms: {
                c: { type: "f", value: 0.3 }, // Intensity coef
                p: { type: "f", value: 3.0 }, // Power (falloff)
                glowColor: { type: "c", value: new THREE.Color(colorA) },
                viewVector: { type: "v3", value: camera.position }
            },
            vertexShader: `
                uniform vec3 viewVector;
                varying float intensity;
                void main() {
                    gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
                    vec3 actual_normal = vec3(modelMatrix * vec4(normal, 0.0));
                    // Calculate intensity based on angle to camera
                    intensity = pow( dot(normalize(viewVector), normalize(actual_normal)), 4.0 );
                }
            `,
            fragmentShader: `
                uniform vec3 glowColor;
                varying float intensity;
                void main() {
                    // Soften the edge
                    gl_FragColor = vec4( glowColor, intensity * 0.6 ); 
                }
            `,
            side: THREE.BackSide,
            blending: THREE.AdditiveBlending,
            transparent: true,
            depthWrite: false
        });

        const glow = new THREE.Mesh(glowGeom, glowMat);
        group.add(glow);

        // Atmosphere / Aura REMOVED per request

        group.position.copy(position);

        return { group, core, glow, flowSpeed };
    };

    // --- Create Systems ---

    // Alpha Centauri A (G-type, similar to Sun)
    // Hot: Bright Yellow/White, Cool: Deep Orange/Gold
    const starA = createStar(6, 0xfffee0, 0xd4af37, new THREE.Vector3(-15, 0, 0), 0.5);
    scene.add(starA.group);

    // Alpha Centauri B (K-type, Orange)
    // Hot: Orange/Yellow, Cool: Reddish-Orange
    const starB = createStar(4.5, 0xffcc33, 0xff4500, new THREE.Vector3(15, 0, -30), 0.7);
    scene.add(starB.group);

    // Proxima Centauri (M-type, Red Dwarf)
    // Hot: Red/Orange, Cool: Deep Red/Dark
    const starProxima = createStar(1.5, 0xff6347, 0x8b0000, new THREE.Vector3(60, 0, -150), 1.2);
    scene.add(starProxima.group);


    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);

    // Animation Loop
    const clock = new THREE.Clock();

    const animate = () => {
        requestAnimationFrame(animate);
        const elapsed = clock.getElapsedTime();

        // Gentle rotation of the whole system
        scene.rotation.y = elapsed * 0.05;
        scene.rotation.x = Math.sin(elapsed * 0.1) * 0.05;

        // Animate each star
        [starA, starB, starProxima].forEach((s, i) => {
            // Bobbing? Maybe less bobbing for top down, just orbit.
            // s.group.position.y += Math.sin(elapsed + i) * 0.02;

            // Shader Animation (Boiling effect)
            if (s.core.material.uniforms) {
                s.core.material.uniforms.time.value = elapsed * s.flowSpeed;
            }

            // Glow facing camera (Critical for top down)
            if (s.glow.material.uniforms) {
                s.glow.material.uniforms.viewVector.value = camera.position;
            }
        });

        // Top Down Orbit Camera
        // Circular orbit in X-Z plane, looking at origin
        const radius = 100;
        const speed = 0.1;
        camera.position.x = Math.sin(elapsed * speed) * radius;
        camera.position.z = Math.cos(elapsed * speed) * radius;
        camera.position.y = 150; // Stable height
        camera.lookAt(scene.position);
        // Fix camera rotation to ensure "up" vector is consistent if needed, 
        // but default up is (0,1,0) which works fine for top-down looking at (0,0,0) usually.
        // Actually, if Looking straight down (0,1,0) -> (0,0,0), up vector (0,1,0) is parallel to view, causing singularity.
        // Since we are at (x, 150, z) looking at (0,0,0), unless x=z=0, we are fine.

        renderer.render(scene, camera);
    };

    animate();

    // Resize Handler
    window.addEventListener('resize', () => {
        width = window.innerWidth;
        height = window.innerHeight;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
    });
}
